import React, { useState, useEffect, useMemo } from 'react';
import Axios from "axios";
import { 
    Grid, Typography, CircularProgress, Container, Box, Button, 
    Card, CardContent, CardActions, Divider, Slider, FormControl, 
    InputLabel, Select, MenuItem, Chip, Paper
} from "@mui/material";
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import DirectionsWalkIcon from '@mui/icons-material/DirectionsWalk';
import MapIcon from '@mui/icons-material/Map';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import FitnessCenterIcon from '@mui/icons-material/FitnessCenter';
import PlaceIcon from '@mui/icons-material/Place';
import config, { environment } from '../../config';

function Tracking() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  
  // Filters
  const [lengthRange, setLengthRange] = useState([0, 50]);
  const [maxDataLength, setMaxDataLength] = useState(50);
  const [difficulty, setDifficulty] = useState('all');

  useEffect(() => {
    const source = Axios.CancelToken.source();
    async function fetchRoutes() {
      try {
        const url = `${config[environment].apiEndpoint}api/thematic-maps/tourist-routes/`;
        const response = await Axios.get(url, { cancelToken: source.token });
        
        // Extract features from GeoJSON
        const features = response.data.tourist_routes?.features || [];
        const extractedRoutes = Array.isArray(features) ? features.map(f => ({
            id: f.id,
            ...f.properties
        })) : [];

        setRoutes(extractedRoutes);
        
        // Set max length for slider
        if (extractedRoutes.length > 0) {
            const maxLen = Math.ceil(Math.max(...extractedRoutes.map(r => r.hossza || 0)));
            setMaxDataLength(maxLen);
            setLengthRange([0, maxLen]);
        }
        
        setLoading(false);
      } catch (error) {
        if (!Axios.isCancel(error)) {
          console.error("Error fetching tourist routes:", error);
          setLoading(false);
        }
      }
    }
    fetchRoutes();
    return () => source.cancel();
  }, []);

  const difficultyOptions = useMemo(() => {
    const diffs = new Set(routes.map(r => r.nehezseg).filter(Boolean));
    return ['all', ...Array.from(diffs)];
  }, [routes]);

  const filteredRoutes = useMemo(() => {
    return routes.filter(route => {
        const routeLen = route.hossza || 0;
        const matchesLength = routeLen >= lengthRange[0] && routeLen <= lengthRange[1];
        const matchesDifficulty = difficulty === 'all' || route.nehezseg === difficulty;
        return matchesLength && matchesDifficulty;
    });
  }, [routes, lengthRange, difficulty]);

  if (loading) {
    return (
      <Grid container justifyContent="center" alignItems="center" sx={{ height: "100vh" }}>
        <CircularProgress />
      </Grid>
    );
  }

  return (
    <Container maxWidth="lg" sx={{ py: 4, mt: '50px' }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 2 }}>
        <Typography variant="h4" sx={{ color: '#105b79', fontWeight: 'bold' }}>
          {t('Thematic.TouristRoutes', 'Turista útvonalak')}
        </Typography>
        <Button 
            variant="contained" 
            startIcon={<MapIcon />}
            onClick={() => {
                if (filteredRoutes.length > 0 && filteredRoutes.length < routes.length) {
                    // Focus on the first filtered route as a representative, or we could implement multiple focus
                    navigate('/turista-utvonalak', { state: { focusRoute: filteredRoutes[0].id } });
                } else {
                    navigate('/turista-utvonalak');
                }
            }}
            sx={{ bgcolor: '#105b79', '&:hover': { bgcolor: '#0d4a63' } }}
        >
            {t('Thematic.ViewOnMap', 'Megtekintés térképen')}
        </Button>
      </Box>

      {/* Filters */}
      <Paper sx={{ p: 3, mb: 4, bgcolor: '#f8f9fa' }}>
        <Grid container spacing={4} alignItems="center">
            <Grid item xs={12} md={6}>
                <Typography gutterBottom sx={{ fontWeight: 'bold' }}>
                    {t('Events.Length', 'Hossz')} (km): {lengthRange[0]} - {lengthRange[1]}
                </Typography>
                <Slider
                    value={lengthRange}
                    onChange={(e, newValue) => setLengthRange(newValue)}
                    valueLabelDisplay="auto"
                    min={0}
                    max={maxDataLength}
                    sx={{ color: '#105b79' }}
                />
            </Grid>
            <Grid item xs={12} md={6}>
                <FormControl fullWidth size="small">
                    <InputLabel>{t('Thematic.Difficulty', 'Nehézség')}</InputLabel>
                    <Select
                        value={difficulty}
                        label={t('Thematic.Difficulty', 'Nehézség')}
                        onChange={(e) => setDifficulty(e.target.value)}
                    >
                        {difficultyOptions.map(opt => (
                            <MenuItem key={opt} value={opt}>
                                {opt === 'all' ? t('Common.All', 'Összes') : opt}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>
            </Grid>
        </Grid>
      </Paper>

      <Grid container spacing={3}>
        {filteredRoutes.length === 0 ? (
          <Typography variant="h6" sx={{ mt: 4, width: '100%', textAlign: 'center', color: 'text.secondary' }}>
            {t('Common.NoResults', 'Nincs a szűrésnek megfelelő találat.')}
          </Typography>
        ) : (
          filteredRoutes.map((route) => (
            <Grid item xs={12} sm={6} md={4} key={route.id}>
              <Card sx={{ height: '100%', display: 'flex', flexDirection: 'column', transition: 'transform 0.2s', '&:hover': { transform: 'scale(1.02)', boxShadow: 6 } }}>
                <CardContent sx={{ flexGrow: 1 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 1, cursor: 'pointer' }} onClick={() => navigate('/turista-utvonalak', { state: { focusRoute: route.id } })}>
                    <DirectionsWalkIcon sx={{ color: '#105b79', mr: 1 }} />
                    <Typography variant="h6" component="div" sx={{ fontWeight: 'bold', color: '#105b79' }}>
                      {route.nev}
                    </Typography>
                  </Box>
                  <Divider sx={{ mb: 2 }} />
                  
                  <Typography variant="body2" color="text.secondary" sx={{ mb: 2, height: '4.5em', overflow: 'hidden', display: '-webkit-box', WebkitLineClamp: 3, WebkitBoxOrient: 'vertical' }}>
                    {route.leiras}
                  </Typography>

                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <AccessTimeIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2">
                            <strong>{t('Events.Length', 'Hossz')}:</strong> {route.hossza} km
                        </Typography>
                    </Box>
                    <Box sx={{ display: 'flex', alignItems: 'center' }}>
                        <FitnessCenterIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                        <Typography variant="body2">
                            <strong>{t('Thematic.Difficulty', 'Nehézség')}:</strong> {route.nehezseg}
                        </Typography>
                    </Box>
                    {route.telep && (
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                            <PlaceIcon fontSize="small" sx={{ mr: 1, color: 'text.secondary' }} />
                            <Typography variant="body2">
                                <strong>{t('Events.Location', 'Helyszín')}:</strong> {route.telep}
                            </Typography>
                        </Box>
                    )}
                  </Box>
                </CardContent>
                <CardActions sx={{ p: 2, pt: 0 }}>
                  <Button 
                    fullWidth 
                    variant="outlined" 
                    startIcon={<MapIcon />}
                    onClick={() => navigate('/turista-utvonalak', { state: { focusRoute: route.id } })}
                    sx={{ color: '#105b79', borderColor: '#105b79' }}
                  >
                    {t('Thematic.ViewOnMap', 'Térkép')}
                  </Button>
                </CardActions>
              </Card>
            </Grid>
          ))
        )}
      </Grid>
    </Container>
  );
}

export default Tracking;